import styled from "styled-components";
import {useAppStore} from "../../../../StateManager";
import type {MissingField} from "../../../5.0/abstract/view/catalog/CatalogItemPanelUtils";
import {colorPalette} from "../../../5.0/styles/colorPalette";
import {baseDistance, fontSize, fontWeight, VerticalFlexStyle, zIndex} from "../../../5.0/styles/styles";
import {IconButton} from "../../../widgets/button/IconButton";

interface IPartialSuccessProps {
	readonly missingFields: MissingField[];
	readonly onClose?: () => void;
}

export const PartialSucesspopup = (props: IPartialSuccessProps) => {
	const appState = useAppStore((state) => state.appState);
	const missingFields: {fieldKey: string; missingValues: string[]}[] = props.missingFields || [];

	return (
		<PartialSucessPopupStyled>
			<div className="title hbox alignCenter">
				<p>The catalog item was partially duplicated</p>
				<IconButton
					className="closeBtn"
					icon="close"
					onClick={props.onClose}
				/>
			</div>

			<div className="body">
				<p className="sub-header">Following values were removed as they are no longer valid.</p>

				<table>
					<thead>
						<tr>
							<th>Field</th>
							<th>Values</th>
						</tr>
					</thead>
					<tbody>
						{missingFields.map((item, index) => {
							const field = appState.actions.getFieldByRefId(item.fieldKey);

							return (
								<tr key={index}>
									<td>{field?.name || item.fieldKey}</td>
									<td>
										{item.missingValues.map((val, i) => {
											const isNotLast = i < item.missingValues.length - 1;

											return (
												<span
													key={i}
													className="value"
												>
													{val}
													{isNotLast ? ",\u00A0" : ""}
												</span>
											);
										})}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</PartialSucessPopupStyled>
	);
};

const PartialSucessPopupStyled = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background-color: ${colorPalette.white};
	box-shadow: 0px 4px 8px 0px #00000080;
	display: flex;
	flex-direction: column;
	z-index: ${zIndex.popup};
	width: 800px;
	min-height: 344px;
	min-height: 300px;
	max-height: 800px;
	flex: 1;

	.title {
		padding: 14px ${baseDistance.md};
		background: #f3f3f3;
		font-size: ${fontSize.lg};
		font-weight: ${fontWeight.normal};
		justify-content: space-between;
		color: #37474f;
		height: 48px;

		> p {
			line-height: 20px;
		}

		.closeBtn {
			cursor: pointer;

			svg {
				width: 12px;
				height: 12px;
			}
		}
	}

	.body {
		${VerticalFlexStyle}
		flex: 1;
		min-height: 0;
		padding: ${baseDistance.md};
	}

	.sub-header {
		font-size: ${fontSize.md};
		font-weight: ${fontWeight.thin};
		color: #37474f;
		line-height: 21px;
	}

	table {
		width: 100%;
		border-collapse: collapse;
		margin-top: 1rem;
		table-layout: fixed;
		${VerticalFlexStyle}
		flex: 1;
		min-height: 0;
		overflow-y: auto;
	}

	th,
	td {
		border: 1px solid ${colorPalette.gray.c100};
		padding: 8px;
		text-align: left;
		font-size: 14px;
		font-weight: ${fontWeight.thin};
		vertical-align: top;
	}

	thead,
	th {
		position: sticky;
		top: 0;
		min-height: 40px;
		background-color: ${colorPalette.white};
	}

	th {
		min-height: 40px;
		color: ${colorPalette.gray.c950};
		font-weight: ${fontWeight.normal};
	}

	th:first-child {
		border-style: solid none solid solid;
	}

	td {
		color: ${colorPalette.gray.c700Dark};
		border-style: none solid solid solid;
	}

	td:first-child {
		color: ${colorPalette.gray.c700Dark};
		border: solid 1px ${colorPalette.gray.c100};
		border-style: none none solid solid;
	}

	th:first-child,
	td:first-child {
		width: 200px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	thead,
	tbody tr {
		display: table;
		width: 100%;
		table-layout: fixed;
	}

	td .value {
		display: inline-block;
		max-width: 540px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		vertical-align: bottom;
	}
`;
